import React, { useState, useEffect } from 'react';
import { message, Modal, Input, Button, InputRef, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  PaymentHeader,
  PaymentStatistics,
} from '../component/maturity-payments';
import { PaymentResponse } from '../model/Payment.model';
import PaymentService from '../service/payment-service/Payment.service';
import useEmployee from '../hook/useEmployee';
import { useIsAdmin } from '../hook/useRole';
import ExportableTable from '../component/common/ExportableTable';
import { exportMaturityPaymentsToExcel } from '../utils/excelExport';
import { useCardDetail } from '../context/CardDetailContext';
import type { FilterDropdownProps } from 'antd/es/table/interface';

const MaturityPayments: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [payments, setPayments] = useState<PaymentResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [cardHolderSearch, setCardHolderSearch] = useState<string | undefined>(
    undefined
  );

  const { employee } = useEmployee();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const { setSelectedCard, setMaturityScheduleId, setMaturityYear } =
    useCardDetail();

  useEffect(() => {
    let isSubscribed = true;
    if (employee?.id) loadPayments(isSubscribed);
    return () => {
      isSubscribed = false;
    };
  }, [
    selectedDate,
    pagination.current,
    pagination.pageSize,
    employee?.id,
    isAdmin,
    cardHolderSearch,
  ]);

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);

  const formatCardNumber = (cardNumber?: string): string =>
    cardNumber ? `***${cardNumber.slice(-4)}` : 'N/A';

  const loadPayments = async (isSubscribed: boolean = true) => {
    if (!selectedDate || !employee?.id) return;
    try {
      setLoading(true);
      const year = selectedDate.year();
      const month = selectedDate.month() + 1;
      const day = selectedDate.date();
      const page = pagination.current - 1;
      const size = pagination.pageSize;
      const response = isAdmin
        ? await PaymentService.getMaturityPaymentsByDate(
            year,
            month,
            day,
            page,
            size,
            cardHolderSearch
          )
        : await PaymentService.getMaturityPaymentsByDateAndEmployee(
            year,
            month,
            day,
            employee.id,
            page,
            size,
            cardHolderSearch
          );
      if (isSubscribed && response.success && response.data) {
        setPayments(response.data.content);
        setPagination((prev) => ({
          ...prev,
          total: response.data.totalElements,
        }));
      } else {
        message.error(response.message || 'Không thể tải danh sách thanh toán');
        setPayments([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch (error) {
      console.error('Error loading payments:', error);
      message.error('Có lỗi xảy ra khi tải danh sách thanh toán');
      setPayments([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(date);
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  };

  const handleRefresh = () => {
    loadPayments();
    message.success('Đã tải lại dữ liệu');
  };

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const handleViewDetail = (data: {
    creditCardId: number;
    creditCardNumber: string;
    maturityScheduleId: number;
    maturityYear: number;
  }) => {
    const payment = payments.find(
      (p) => p.maturitySchedule?.creditCard?.id === data.creditCardId
    );
    if (payment?.maturitySchedule?.creditCard) {
      setSelectedCard(payment.maturitySchedule.creditCard);
      setMaturityScheduleId(data.maturityScheduleId);
      setMaturityYear(data.maturityYear);
      navigate('/card-detail');
    }
  };

  const handleDeletePayment = (payment: PaymentResponse) => {
    Modal.confirm({
      title: 'Xác nhận xóa thanh toán',
      content: `Bạn có chắc chắn muốn xóa thanh toán #${payment.id}?`,
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await PaymentService.deletePayment(payment.id, employee?.id as number);
          if (response.success) {
            message.success('Đã xóa thanh toán thành công');
            loadPayments();
          } else {
            message.error(response.message || 'Không thể xóa thanh toán');
          }
        } catch (error) {
          console.error('Error deleting payment:', error);
          message.error('Có lỗi xảy ra khi xóa thanh toán');
        }
      },
    });
  };

  const getUniqueValues = (field: string) => {
    return Array.from(
      new Set(
        payments
          .map((p: any) => field.split('.').reduce((obj, key) => obj?.[key], p))
          .filter(Boolean)
      )
    );
  };

  const statusLabels: Record<string, string> = {
    COMPLETED: 'Hoàn thành',
    PENDING: 'Đang chờ',
  };

  const createFilters = (values: any[]) =>
    values.map((v) => ({
      text: statusLabels[v] || v, // hiển thị tiếng Việt
      value: v, // giữ nguyên giá trị gốc để so sánh
    }));

  const columns = [
    {
      title: 'Ngày thanh toán',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : []);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            format="DD/MM/YYYY"
            disabledDate={(current) => {
              // Chỉ cho phép chọn ngày trong tháng đã chọn
              return (
                !current ||
                current.month() !== selectedDate.month() ||
                current.year() !== selectedDate.year()
              );
            }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) => {
        const recordDate = dayjs(record.paymentDate).format('YYYY-MM-DD');
        return recordDate === value;
      },
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      sorter: (a: any, b: any) =>
        dayjs(a.paymentDate).unix() - dayjs(b.paymentDate).unix(),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={(e) => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        record.amount.toString().includes(value),
      render: (amount: number) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(amount)}
        </span>
      ),
      sorter: (a: any, b: any) => a.amount - b.amount,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'originalStatus',
      key: 'originalStatus',
      width: 120,
      filters: createFilters(getUniqueValues('originalStatus')),
      onFilter: (value: string, record: any) => record.originalStatus === value,
      render: (status: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
        >
          {status === 'COMPLETED' ? 'Hoàn thành' : 'Chờ xử lý'}
        </span>
      ),
    },
    {
      title: 'Nhân viên',
      dataIndex: ['employee', 'name'],
      key: 'employee',
      width: 150,
      filters: createFilters(getUniqueValues('employee.name')),
      onFilter: (value: string, record: any) => record.employee?.name === value,
      render: (name: string) => <span className="text-gray-800">{name}</span>,
    },
    {
      title: 'Khách hàng',
      dataIndex: ['customer', 'fullName'],
      key: 'customer',
      width: 200,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm khách hàng"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={(e) => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        (record.customer?.fullName || '')
          .toLowerCase()
          .includes((value || '').toLowerCase()),
      render: (name: string) => <span className="text-gray-800">{name}</span>,
    },
    {
      title: 'Số thẻ',
      dataIndex: ['maturitySchedule', 'creditCard', 'cardNumber'],
      key: 'cardNumber',
      width: 150,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số thẻ"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={(e) => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      onFilter: (value: string, record: any) =>
        (record.maturitySchedule?.creditCard?.cardNumber || '')
          .toLowerCase()
          .includes((value || '').toLowerCase()),
      render: formatCardNumber,
    },
    {
      title: 'Chủ thẻ',
      dataIndex: ['maturitySchedule', 'creditCard', 'cardHolderName'],
      key: 'cardHolderName',
      width: 200,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm chủ thẻ"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              setCardHolderSearch(selectedKeys[0] as string);
              setPagination((prev) => ({ ...prev, current: 1 }));
              confirm();
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => {
              setCardHolderSearch(selectedKeys[0] as string);
              setPagination((prev) => ({ ...prev, current: 1 }));
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => {
              clearFilters && clearFilters();
              setCardHolderSearch(undefined);
              setPagination((prev) => ({ ...prev, current: 1 }));
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
      ),
      render: (name: string) => (
        <span className="text-gray-700">{name || 'N/A'}</span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_: any, record: PaymentResponse) => (
        <div className="space-x-2">
          <button
            onClick={() =>
              handleViewDetail({
                creditCardId: record.maturitySchedule?.creditCard?.id || 0,
                creditCardNumber:
                  record.maturitySchedule?.creditCard?.cardNumber || '',
                maturityScheduleId: record.maturitySchedule?.id || 0,
                maturityYear: dayjs(record.maturitySchedule?.dueDate).year(),
              })
            }
            className="text-purple-600 hover:text-purple-800 hover:underline"
          >
            Chi tiết
          </button>
        </div>
      ),
    },
  ];

  const handleExport = async (selectedRecords: PaymentResponse[]) => {
    await exportMaturityPaymentsToExcel(
      selectedRecords,
      selectedDate.month() + 1,
      selectedDate.year()
    );
  };

  return (
    <div className="space-y-6 p-6">
      <PaymentHeader
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        onRefresh={handleRefresh}
        loading={loading}
      />
      <PaymentStatistics
        selectedDate={selectedDate}
        employeeId={employee?.id}
        isAdmin={isAdmin}
      />
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <ExportableTable
          columns={columns}
          dataSource={payments}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handlePaginationChange,
            showSizeChanger: false,
          }}
          onExport={handleExport}
          exportTitle="Xuất báo cáo thanh toán đáo hạn"
          isAdmin={isAdmin}
        />
      </div>
    </div>
  );
};

export default MaturityPayments;
